import { LitElement, html, css, styleMap, utils } from '@hh/global.js'

/**
 * scroll state -1: haven't scrolled yet
 * scroll state 0: initial scroll down
 * scroll state 1: scrolling down, past a certain point
 * scroll state 2: scrolling up
 */

class HHStickyHeader extends LitElement {
  static properties = {
    _headerHeight: {
      type: Number,
      state: true,
    },
    _announcementHeight: {
      type: Number,
      state: true,
    },
    _subNavHeight: {
      type: Number,
      state: true,
    },
    _state: {
      type: Number,
      state: true,
    },
    _height: {
      type: Number,
      state: true,
    },
    swans: {
      type: Boolean,
    },
    blog: {
      type: Boolean,
    }
  }

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0

    this._state = -1
  }

  connectedCallback() {
    super.connectedCallback()
    this.onScrollHandler = this.onScroll.bind(this)
    this.setSpacerHandler = this.setSpacerHeight.bind(this)
    window.addEventListener('scroll', this.onScrollHandler, {passive: true});
    window.addEventListener('resize', this.setSpacerHandler);
    document.addEventListener('openOrbe', this.handleOpenOrbe);
    this.createObserver()
    setTimeout(() => {
      this.setSpacerHeight()
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.onScrollHandler)
    window.removeEventListener('resize', this.setSpacerHandler);
    document.removeEventListener('openOrbe', this.handleOpenOrbe);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  setSpacerHeight() {
    const headerHeight = this.querySelector('.js-header')?.getBoundingClientRect().height
    const subNavHeight = this.querySelector('.js-sub-nav')?.getBoundingClientRect().height || 0
    const primarySubNavHeight = this.querySelector('.js-primary-sub-nav')?.getBoundingClientRect().height || 0
    const announcementHeight = this.querySelector('.js-announcement-bar')?.getBoundingClientRect().height || 0
    const mobileBlogBarHeight = document.querySelector('.js-blog-bar-mobile')?.getBoundingClientRect().height || 0
    const blogMenuHeight = this.querySelector('.js-blog-menu')?.getBoundingClientRect().height || 0
    this._headerHeight = headerHeight
    this._subNavHeight = subNavHeight
    this._announcementHeight = announcementHeight
    this._blogMenuHeight = blogMenuHeight

    document.querySelector(':root').style.setProperty(`--hh-announcement-height`, `${announcementHeight}px`);
    document.querySelector(':root').style.setProperty(`--hh-header-height`, `${headerHeight}px`);
    document.querySelector(':root').style.setProperty(`--hh-mobile-blog-bar-height`, `${mobileBlogBarHeight}px`);
    document.querySelector(':root').style.setProperty(`--hh-sub-nav-height`, `${subNavHeight}px`);
    document.querySelector(':root').style.setProperty(`--hh-primary-sub-nav-height`, `${primarySubNavHeight}px`);

    this._height = announcementHeight
    this._state = 0

    if (this.blog) {
      document.querySelector("main").style.paddingTop = `${headerHeight + blogMenuHeight - 1}px`;
      document.querySelector(':root').style.setProperty(`--hh-blog-gradient-height`, `${headerHeight + blogMenuHeight}px`);
    } else if (window.location.href.indexOf('pages/swans-bar') < 0) {
      document.querySelector("main").style.paddingTop = `${headerHeight + announcementHeight + subNavHeight + primarySubNavHeight - 1}px`;
    }
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    if (this.swans) {
      this.classList[window.scrollY > 0 ? 'add' : 'remove']('scrolled')
      return
    }

    if (this.blog) {
      const headerHeight = this.querySelector('.js-header')?.getBoundingClientRect().height
      document.querySelector(':root').style.setProperty(`--hh-header-height`, `${headerHeight}px`);
      this.classList[window.scrollY > 0 ? 'add' : 'remove']('scrolled')
      return;
    }
    
    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll down
      requestAnimationFrame(this.hide.bind(this))
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      // scroll up
      requestAnimationFrame(this.reveal.bind(this))
    } else if (scrollTop <= this.headerBounds.top) {
      // at top
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    document.querySelector(':root').style.setProperty(`--hh-announcement-height`, `0px`);

    utils.all(this.closeWhenHidden, this.header).map((element) => {
      element.setAttribute('open', false)
    })
  }

  reveal() {
    if (this._state === 2) {
      return
    }
    document.querySelector(':root').style.setProperty(`--hh-announcement-height`, `${this._announcementHeight}px`);

    this._state = 2
  }

  reset() {
    if (this._state === 0) {
      return
    }
    
    this._state = 0
  }

  handleOpenOrbe() {
    setTimeout(() => {
      orbito.openAndRefreshModal();
      document.querySelector('.md-modal-closeButton')?.focus();
    }, 10)
  }

  render() {
    return html`<div
        class="container"
        style="${styleMap({
          transform: this._state === 1 ? `translateY(-${this._height}px)` : 'translateY(0)',
        })}"
      >
        <slot></slot>
      </div>
      `
  }

  static styles = css`
    .container {
      position: fixed;
      top: 0;
      transform: translateY(0);
      transition: transform var(--hh-duration-long, 500ms);
      width: 100%;
      z-index: 2;
    }
  `
}

customElements.define('hh-sticky-header', HHStickyHeader)
